// src/pages/InventoryLogs.jsx
import React, { useEffect, useState } from "react";
import CustomListing from "../components/CustomListing";
import { useOrderApi } from "../api/orders";
import "../styles/layout.css";
import ReusableHeader from "../components/ReusableHeader";
import ReusableFilter from "../components/ReusableFilter";
import { toast } from "react-toastify";
import CustomButton from "../components/CustomButton";
import CustomDropdown from "../components/CustomDropdown";
import { Filter } from "lucide-react";
import { decodeJwt } from "../util/decodeJwt";
import { formatDate } from "../util/date";
import { useDeviceApi } from "../api/device";
import { useOrganizationApi } from "../api/organization";
import { Trash, Pencil } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function InventoryLogs() {
  const { getAllVendorOrgAndCustomers } = useOrganizationApi()
  const { getAllVendorOrders, updateOrderStatus, deleteOrder } = useOrderApi()
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [currentUserOrgId, setCurrentUserOrgId] = useState("");
  const [expandedRows, setExpandedRows] = useState({});
  const navigate = useNavigate();

  // Filter selections
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [selectedToOrg, setSelectedToOrg] = useState([]);
  const [selectedFromOrg, setSelectedFromOrg] = useState([]); // ✅ ADD
  const [selectedStatus, setSelectedStatus] = useState("");

  // Filter options
  const [orgOptions, setOrgOptions] = useState([]);
  const statusOptions = ["pending", "accept", "in-transit", "delivered", "cancelled", "returned"];
  const [searchText, setSearchText] = useState("");

  // --- Date Filters ---
  const today = new Date();
  const currentDate = today.toISOString().split("T")[0]; // e.g., "2025-09-26"
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [fromOrgOptions, setFromOrgOptions] = useState([]);
  const [toOrgOptions, setToOrgOptions] = useState([]);

  // Initialize filterObject as empty to show all records by default
  const [filterObject, setFilterObject] = useState({});


  const statusStyles = {
    pending: { color: "#b71c1c", backgroundColor: "#ffcdd2" },
    accept: { color: "#ff6f00", backgroundColor: "#ffe0b2" },
    "in-transit": { color: "#1b5e20", backgroundColor: "#c8e6c9" },
    delivered: { color: "#004d40", backgroundColor: "#b2dfdb" },
    cancelled: { color: "#f57f17", backgroundColor: "#fff9c4" },
    returned: { color: "#0d47a1", backgroundColor: "#bbdefb" },
  };

  // Decode user token
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      const decoded = decodeJwt(token);
      setCurrentUserOrgId(decoded.organizationId);
    }
  }, []);

  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const res = await getAllVendorOrgAndCustomers();

        if (!res?.success) throw new Error("API failed");

        // ✅ FROM options
        setFromOrgOptions(
          (res.from || []).map(o => ({
            value: o.key,
            label: o.value
          }))
        );

        // ✅ TO options
        setToOrgOptions(
          (res.to || []).map(o => ({
            value: o.key,
            label: o.value
          }))
        );

      } catch (err) {
        console.error(err);
        toast.error("Failed to load filter options");
      }
    };

    fetchFilters();
  }, []);



  const getNextStatusOptions = (row) => {
    const isParent = row.fromOrganization?._id === currentUserOrgId;
    if (isParent) {
      if (row.status === "pending") return ["pending", "cancelled"];
      if (row.status === "accept") return ["accept", "in-transit"];
      if (row.status === "in-transit") return ["in-transit", "cancelled"];
      if (row.status === "delivered") return ["delivered"];
      return [row.status];
    } else {
      if (row.status === "pending") return ["pending", "accept", "cancelled"];
      if (row.status === "accept") return ["accept"];
      if (row.status === "in-transit") return ["in-transit", "delivered", "cancelled"];
      if (row.status === "delivered") return ["delivered"];
      return [row.status];
    }
  };

  const columns = [
    {
      key: "serialNumber",
      label: "BCU Serial Number",
      render: (row) =>
        (row.devices || [row.device])
          .map((d) => d.serialNumber || "N/A")
          .join(", "),
    },
    {
      key: "manufacturerId",
      label: "Machine Serial Number",
      render: (row) =>
        (row.devices || [row.device])
          .map((d) => d.manufacturerId || "N/A")
          .join(", "),
    },
    {
      key: "devices",
      label: "Devices",
      render: row =>
        formatDevices(row.devices || [row.device], row._id),
    },
    {
      key: "fromOrganization",
      label: "From Address",
      render: row =>
        row.fromOrganization?.address ||
        row.fromOrganization?.name ||
        "-"
    },
    {
      key: "toOrganization",
      label: "To Address",
      render: row =>
        row.toOrganization?.address ||
        row.toUser?.name ||
        "-"
    },
    { key: "createdAt", label: "Created At", render: row => formatDate(row.createdAt) },
    {
      key: "status",
      label: "Status",
      render: row => {
        const options = getNextStatusOptions(row);
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <select
              value={row.status}
              onChange={(e) => handleStatusChange(row._id, e.target.value)}
              style={{
                padding: "4px 8px",
                borderRadius: "6px",
                fontWeight: "bold",
                textTransform: "capitalize",
                cursor: "pointer",
                backgroundColor: statusStyles[row.status]?.backgroundColor || "#eee",
                color: statusStyles[row.status]?.color || "#000",
                border: "none"
              }}
            >
              {options.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            {row.hasProduction && (
              <span style={{
                fontSize: "11px",
                color: "#2e7d32",
                display: "flex",
                alignItems: "center",
                gap: "4px",
                fontWeight: "600",
                marginTop: "2px"
              }}>
                <span style={{
                  width: "6px",
                  height: "6px",
                  backgroundColor: "#2e7d32",
                  borderRadius: "50%",
                  display: "inline-block"
                }}></span>
                Production Active
              </span>
            )}
          </div>
        );
      }
    },
    {
      key: "actions",
      label: "Actions",
      render: (row) => {
        const isOwner = row.fromOrganization?._id === currentUserOrgId;
        if (!isOwner) return null;

        return (
          <div className="action-buttons">
            <button
              className="delete-btn"
              onClick={() => handleDeleteLog(row._id)}
              disabled={row.hasProduction}
              style={{
                opacity: row.hasProduction ? 0.3 : 1,
                cursor: row.hasProduction ? "not-allowed" : "pointer"
              }}
              title={row.hasProduction ? "Cannot delete: Device has production records" : "Delete Log"}
            >
              <Trash size={16} />
            </button>
          </div>
        );
      },
    }
  ];

  const formatDevices = (devices, rowId) => {
    if (!devices?.length) return "";

    const macs = devices.map(d => d.macAddress || "N/A");

    const isExpanded = expandedRows[rowId];

    return (
      <div className="device-cell">

        {/* FIRST LINE */}
        <div className="device-main-line">
          <span>{macs[0]}</span>

          {macs.length > 1 && (
            <span
              className="expand-toggle"
              onClick={() =>
                setExpandedRows(prev => ({
                  ...prev,
                  [rowId]: !prev[rowId],
                }))
              }
            >
              {isExpanded ? " ▲" : " ▼"}{" "}
              {!isExpanded && `& ${macs.length - 1} more`}
            </span>
          )}
        </div>

        {/* EXPANDED LIST BELOW */}
        {isExpanded && (
          <div className="device-expand-list">
            {macs.slice(1).map((m, i) => (
              <div key={i}>{m}</div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const fetchWithFilters = async (params) => {
    return await getAllVendorOrders({
      ...params,
      ...filterObject,
      search: searchText,
    });
  };


  // APPLY FILTERS
  const handleApplyFilters = async () => {
    const newFilters = {};

    if (selectedDevices.length > 0) {
      newFilters.deviceIds = selectedDevices;
    }

    // ✅ FROM ORG FILTER
    if (selectedFromOrg?.length > 0) {
      newFilters.fromOrgId = selectedFromOrg;
      newFilters.fromOrgName = selectedFromOrg.map(sel =>
        fromOrgOptions.find(o => o.value === sel)?.label || sel
      );
    }

    if (selectedToOrg?.length > 0) {
      newFilters.toOrgId = selectedToOrg;
      newFilters.toOrgName = selectedToOrg.map(sel =>
        toOrgOptions.find(o => o.value === sel)?.label || sel
      );
    }

    // --- Date filters ---
    if (fromDate) newFilters.fromDate = fromDate;
    if (toDate) newFilters.toDate = toDate;

    setFilterObject({ ...newFilters });
    setIsFilterOpen(false);
    toast.success("Filters applied");

    try {
      const res = await getAllVendorOrders({ limit: 10, offset: 0, ...newFilters, search: searchText });
      if (res?.success) setRows(res.data);
    } catch (err) {
      toast.error("Failed to fetch filtered data");
      console.error(err);
    }
  };

  // REMOVE FILTER
  const handleRemoveFilter = async (key) => {
    const updated = { ...filterObject };
    delete updated[key];

    if (key === "deviceIds") { setSelectedDevices([]); delete updated.deviceNames; }
    if (key === "fromOrgId") {
      setSelectedFromOrg([]);
      delete updated.fromOrgName;
    }
    if (key === "toOrgId") { setSelectedToOrg([]); delete updated.toOrgName; }
    if (key === "fromDate") setFromDate(currentDate);
    if (key === "toDate") setToDate(currentDate);

    setFilterObject(updated);

    try {
      const res = await getAllVendorOrders({ limit: 10, offset: 0, ...updated, search: searchText });
      if (res?.success) setRows(res.data);
    } catch (err) {
      toast.error("Failed to fetch data after removing filter");
      console.error(err);
    }
  };

  // DELETE ORDER
  const handleDeleteLog = async (orderId) => {
    if (!window.confirm("Delete this order?")) return;

    try {
      await deleteOrder(orderId);
      toast.success("Order deleted");

      fetchWithFilters({ limit: 10, offset: 0 }).then(res => {
        if (res?.success) setRows(res.data);
      });

    } catch (err) {
      toast.error(err.message);
    }
  };


  // EDIT ORDER
  const handleEditLog = (row) => {
    // OPTION 1 → navigate to edit page
    navigate(`/inventory-logs/edit/${row._id}`);

    // OPTION 2 → open modal (tell me if you want)
  };

  // ACTIVE FILTERS CHIP
  const activeFilters = [];
  if (filterObject.deviceNames?.length)
    activeFilters.push({ key: "deviceIds", label: "Devices", value: filterObject.deviceNames.join(", ") });
  if (filterObject.fromOrgName?.length)
    activeFilters.push({
      key: "fromOrgId",
      label: "From Org",
      value: filterObject.fromOrgName.join(", ")
    });
  if (filterObject.toOrgName?.length)
    activeFilters.push({ key: "toOrgId", label: "To Org", value: filterObject.toOrgName.join(", ") });
  if (filterObject.fromDate) activeFilters.push({ key: "fromDate", label: "From Date", value: filterObject.fromDate });
  if (filterObject.toDate) activeFilters.push({ key: "toDate", label: "To Date", value: filterObject.toDate });

  const renderActionButtons = () => (
    <div className="header-buttons" style={{ display: "flex", alignItems: "center" }}>
      <input type="text" placeholder="Search inventory logs..." value={searchText} onChange={e => setSearchText(e.target.value)} className="search-input" />
      <CustomButton className="header-btn" label={<Filter size={16} />} onClick={() => setIsFilterOpen(true)} />
    </div>
  );

  const handleStatusChange = async (rowId, newStatus) => {
    try {
      const res = await updateOrderStatus({ orderId: rowId, status: newStatus });
      if (res.success) {
        toast.success("Status updated!");
        setRows(prev => prev.map(r => r._id === rowId ? { ...r, status: newStatus } : r));
      } else toast.error(res.message || "Failed to update status");
    } catch {
      toast.error("Failed to update status");
    }
  };

  return (
    <div className="inventoryManagement">
      <ReusableHeader title="Inventory Logs" children={renderActionButtons()} />

      {/* Active filter chips */}
      <div className="filter-chips-container">
        {activeFilters.map((f, i) => (
          <div key={i} className="filter-chip">
            {f.label}: {f.value}
            <span className="remove-icon" onClick={() => handleRemoveFilter(f.key)}>✕</span>
          </div>
        ))}
      </div>

      <CustomListing
        showCheckbox={false}
        data={rows}
        setData={setRows}
        fetchData={fetchWithFilters}
        columns={columns}
        pageSize={10}
        postData={{ ...filterObject, search: searchText }}
      />

      {/* Filter modal */}
      <ReusableFilter isOpen={isFilterOpen} onClose={() => setIsFilterOpen(false)} title="Filter Orders">
        <div className="filter-fields">
          {/* Date filters */}
          <div className="filter-field">
            <label className="filter-label">From Date</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="date-input fancy-date"
            />
          </div>

          <div className="filter-field">
            <label className="filter-label">To Date</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="date-input fancy-date"
            />
          </div>
          <CustomDropdown
            label="From Org"
            multi
            value={selectedFromOrg}
            onChange={setSelectedFromOrg}
            options={fromOrgOptions}
          />

          <CustomDropdown
            label="To Org"
            multi
            value={selectedToOrg}
            onChange={setSelectedToOrg}
            options={toOrgOptions}
          />
          <div className="filter-actions">
            <CustomButton className="apply-btn" label="Apply" onClick={handleApplyFilters} />
            <CustomButton className="clear-btn" label="Clear" onClick={() => {
              setSelectedDevices([]);
              setSelectedToOrg([]);
              setSelectedStatus("");
              setFromDate(currentDate);
              setToDate(currentDate);
              setFilterObject({});
              setIsFilterOpen(false);
            }} />
          </div>
        </div>
      </ReusableFilter>
    </div>
  );
}
